import {
  getStoryOpenTab,
  hasUploadFailed,
  isStoryEditorEntryReady,
  isStoryReady,
} from '@src/components/Dashboard/utils';
import { useDashboard, useDatoPerson } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { ContentTab, Studio } from '@src/types.ts/general';
import { formatDateTimeCompact } from '@src/utility/dateFormat';
import { extractErrorInfo } from '@src/utility/error';
import { getAvailableClipFormats, getPosterUrl } from '@src/utility/general';
import { LogManager } from '@src/utility/Logger';
import { fetchSignedUrls } from '@src/utility/story';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { getClipUpdatedAt } from './contentItems';
import { HomeStoryCard } from './HomeStoryCard';
import { SkeletonStoryCard } from './SkeletonPrimitives';

import type { StoryDimension } from './HomeStoryCard';
import type { DashboardStory, OtherVideo } from '@src/types.ts/story';

const logger = LogManager.getLogger('feature:RecentStories');

const MAX_STORIES = 5;

export const RecentStories = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const datoPerson = useDatoPerson();
  const dashboardStore = useDashboard();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Use datoPerson.selectedOrganization for faster loading (like Dashboard does)
  const album = datoPerson.selectedOrganization || videoCreator.organization;

  // Read from store cache
  const { homeStories, homeStoriesAlbumId, homeStoriesLoading } =
    dashboardStore;

  // Derive display stories from cache (sorted and limited). PLAT-11224:
  // membership starts at upload time — still-processing stories render as
  // placeholder cards (no thumbnail/duration yet, processing badge) and turn
  // clickable at transcription-ready. Only failed uploads stay hidden.
  const stories = useMemo(() => {
    return homeStories
      .filter(story => !hasUploadFailed(story))
      .sort((a, b) => {
        const dateA = new Date(a._publishedAt || 0);
        const dateB = new Date(b._publishedAt || 0);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, MAX_STORIES);
  }, [homeStories]);

  // Load lifecycle for the album this mount has fetched. Guards the effect's
  // re-runs (each setHomeStories changes its deps): 'loading' means a fetch is
  // in flight, 'done' + intact cache means nothing to do. A 'done' album whose
  // cache was invalidated (e.g. clearHomeCache call) reloads.
  // Writes from late responses only land while the album still matches, so an
  // org switch mid-fetch can't overwrite the new org's data.
  const loadStateRef = useRef<{
    albumId: string;
    status: 'loading' | 'done';
  } | null>(null);

  useEffect(() => {
    if (!album?.id) return;
    const albumId = album.id;

    // Stale-while-revalidate: with cached data for this album, render it
    // immediately (no skeletons) and refresh silently in the background.
    const hasCache = homeStoriesAlbumId === albumId && homeStories.length > 0;

    const state = loadStateRef.current;
    if (state?.albumId === albumId) {
      if (state.status === 'loading') return;
      if (hasCache) return;
    }

    loadStateRef.current = { albumId, status: 'loading' };
    dashboardStore.setHomeStoriesLoading(!hasCache);

    // Signed thumbnail URLs for AWS stories via the lightweight sign endpoint
    // (same as Story Manager). Runs after first paint — thumbnails/durations
    // for AWS stories pop in when it lands instead of blocking the page.
    const enrichStories = async (fetchedStories: DashboardStory[]) => {
      const awsStoryIds = fetchedStories
        .filter((s: DashboardStory) => s.useAws)
        .map((s: DashboardStory) => s.id);
      if (!awsStoryIds.length) return;

      const signedUrls = await fetchSignedUrls(awsStoryIds);
      // Skip if the album changed or the cache was invalidated mid-flight —
      // writing then would resurrect a stale story list.
      if (loadStateRef.current?.albumId !== albumId) return;
      if (dashboardStore.homeStoriesAlbumId !== albumId) return;

      // Map the live cache, not the pre-await payload: a delete that landed
      // while the sign request was in flight must survive this write.
      const homeStories = dashboardStore.homeStories;
      const enrichedStories = homeStories.map((story: DashboardStory) => {
        const signed = signedUrls[story.id];
        if (!story.useAws || !signed) return story;
        const video = {
          duration: signed.duration,
          thumbnailUrl: signed.thumbnailUrl,
          mp4Url: '',
          framerate: 0,
        };
        // Home reads originalVideo for thumbnails/durations, so point it at
        // the signed data for AWS stories (Dato's originalVideo is empty).
        return {
          ...story,
          originalVideo: { video },
          selfHostedVideo: {
            url: '',
            id: story.id,
            height: 0,
            width: 0,
            video,
            customData: null,
          },
        };
      });
      dashboardStore.setHomeStories(enrichedStories, albumId);
    };

    const fetchStories = async () => {
      try {
        const fetchedStories = await videoCreator.findManyStories(albumId);
        if (loadStateRef.current?.albumId !== albumId) return;

        // Carry already-signed AWS video data over from the previous cache so
        // a silent refresh doesn't blank enriched thumbnails until the new
        // sign request lands (enrichStories re-signs everything right after).
        const prior = new Map<string, DashboardStory>();
        dashboardStore.homeStories.forEach(s => prior.set(s.id, s));
        const mergedStories = fetchedStories.map((s: DashboardStory) => {
          const prev = prior.get(s.id);
          return s.useAws && prev?.selfHostedVideo
            ? {
                ...s,
                originalVideo: prev.originalVideo,
                selfHostedVideo: prev.selfHostedVideo,
              }
            : s;
        });

        // Paint first: cache the stories and drop the skeletons before
        // enrichment — first render only needs the GraphQL payload.
        dashboardStore.setHomeStories(mergedStories, albumId);
        dashboardStore.setHomeStoriesLoading(false);
        loadStateRef.current = { albumId, status: 'done' };

        await enrichStories(mergedStories);
      } catch (error) {
        logger.error('Failed to fetch stories', extractErrorInfo(error));
        if (loadStateRef.current?.albumId === albumId) {
          dashboardStore.setHomeStoriesLoading(false);
          loadStateRef.current = { albumId, status: 'done' };
        }
      }
    };

    void fetchStories();
  }, [
    album?.id,
    videoCreator,
    dashboardStore,
    homeStoriesAlbumId,
    homeStories.length,
  ]);

  const viewAllStoriesPath = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return `/${Studio.dashboard}?${params.toString()}`;
  }, [searchParams]);

  const header = (
    <HeaderRow>
      <SectionHeader>Recent Stories</SectionHeader>
      <ViewAllLink to={viewAllStoriesPath}>View All Stories</ViewAllLink>
    </HeaderRow>
  );

  const handleStoryClick = useCallback(
    (story: DashboardStory) => {
      const params = new URLSearchParams(searchParams);
      params.set('storyId', story.id);
      params.set('tab', getStoryOpenTab(story));
      navigate(`/content?${params.toString()}`);
      window.scrollTo(0, 0);
    },
    [navigate, searchParams],
  );

  // Deep-links into the story's clips tab with the clip preview open on the
  // clicked aspect ratio (selectedDimension seeds the preview's dimension
  // selector). The target clip is chosen up-front in getStoryDimensionData.
  const openDimensionPreview = useCallback(
    (storyId: string, aspectRatio: string, video: OtherVideo) => {
      videoCreator.videoClipPreview = {
        ...video,
        selectedDimension: aspectRatio,
      };

      const params = new URLSearchParams(searchParams);
      params.set('storyId', storyId);
      params.set('tab', ContentTab.video_clip);
      navigate(`/content?${params.toString()}`);
      window.scrollTo(0, 0);
    },
    [navigate, searchParams, videoCreator],
  );

  const formatUpdateDate = (dateString?: string) => {
    if (!dateString) return undefined;
    return formatDateTimeCompact(dateString) || undefined;
  };

  const getStoryDuration = (story: DashboardStory): string | undefined => {
    const finalVideoDuration =
      story.finalVideo?.videoFilePrimary?.video?.duration;
    if (finalVideoDuration) return convertTimeToMMSS(finalVideoDuration);
    const originalVideoDuration = story.originalVideo?.video?.duration;
    if (originalVideoDuration)
      return convertTimeToMMSS(Number(originalVideoDuration));
    return undefined;
  };

  // Matches Dashboard's getStoryThumbnailUrl pattern
  const getStoryThumbnail = (story: DashboardStory): string | undefined => {
    // Try story thumbnail first
    if (story.thumbnail?.url) {
      return getPosterUrl(story.thumbnail.url, 480, 270);
    }
    // Try final video thumbnail
    if (story.finalVideo?.thumbnail?.url) {
      return getPosterUrl(story.finalVideo.thumbnail.url, 480, 270);
    }
    // Try final video Mux thumbnail
    const finalThumb = story.finalVideo?.videoFilePrimary?.video?.thumbnailUrl;
    if (finalThumb) return finalThumb;
    // Try original video Mux thumbnail (populated by requestMissingFieldsBatch)
    return story.originalVideo?.video?.thumbnailUrl;
  };

  // Hidden clips are soft-deleted ("removed"), so they don't count — the same
  // filter getStoryDimensionData below and every other clip listing applies.
  const getClipsCount = (story: DashboardStory): number => {
    return story.otherVideos?.filter(video => !video.isHidden).length || 0;
  };

  // Single pass over the story's (non-hidden) clips that produces both the
  // badge stats and the clip each badge opens on click:
  // - dimensions: the union of aspect ratios; a ratio counts as published when
  //   at least one clip has a rendered file in it (the same per-clip signal
  //   Content Studio's DimensionBadge uses), and clipCount is how many clips
  //   offer it (getAvailableClipFormats dedupes ratios within a clip, so each
  //   clip contributes at most once).
  // - targetByRatio: the clip to open for a ratio — prefers a clip whose
  //   variant for that ratio has a rendered file (published), then the most
  //   recently updated.
  const getStoryDimensionData = (
    story: DashboardStory,
  ): {
    dimensions: StoryDimension[];
    targetByRatio: Map<string, OtherVideo>;
  } => {
    const statsByRatio = new Map<
      string,
      { isPublished: boolean; clipCount: number }
    >();
    const targetByRatio = new Map<
      string,
      { video: OtherVideo; isPublished: boolean; updatedAt: number }
    >();

    story.otherVideos
      ?.filter(video => !video.isHidden)
      .forEach(video => {
        // Same "last human action" notion the Recent Content table uses, so
        // "most recently updated" means the same thing for the badge target.
        const updatedAt = getClipUpdatedAt(video).getTime();
        getAvailableClipFormats(video).forEach(format => {
          if (!format.aspectRatio) return;
          const ratio = format.aspectRatio;

          const entry = statsByRatio.get(ratio) ?? {
            isPublished: false,
            clipCount: 0,
          };
          entry.isPublished = entry.isPublished || format.isPublished;
          entry.clipCount += 1;
          statsByRatio.set(ratio, entry);

          const best = targetByRatio.get(ratio);
          if (
            !best ||
            (format.isPublished && !best.isPublished) ||
            (format.isPublished === best.isPublished &&
              updatedAt > best.updatedAt)
          ) {
            targetByRatio.set(ratio, {
              video,
              isPublished: format.isPublished,
              updatedAt,
            });
          }
        });
      });

    const dimensions = Array.from(statsByRatio, ([aspectRatio, stats]) => ({
      aspectRatio,
      ...stats,
    })).sort((a, b) => a.aspectRatio.localeCompare(b.aspectRatio));

    return {
      dimensions,
      targetByRatio: new Map(
        Array.from(targetByRatio, ([ratio, best]) => [ratio, best.video]),
      ),
    };
  };

  const getArticlesCount = (story: DashboardStory): number => {
    return story.savedBlog ? Object.keys(story.savedBlog).length : 0;
  };

  if (homeStoriesLoading) {
    return (
      <Section data-testid="home-recent-stories">
        {header}
        <StoriesGrid data-testid="home-recent-stories-grid">
          {Array.from({ length: MAX_STORIES }, (_, i) => (
            <SkeletonStoryCard key={i} />
          ))}
        </StoriesGrid>
      </Section>
    );
  }

  if (stories.length === 0) {
    return (
      <Section data-testid="home-recent-stories">
        {header}
        <EmptyText>
          No stories yet. Create your first story to get started!
        </EmptyText>
      </Section>
    );
  }

  return (
    <Section data-testid="home-recent-stories">
      {header}
      <StoriesGrid data-testid="home-recent-stories-grid">
        {stories.map(story => {
          const { dimensions, targetByRatio } = getStoryDimensionData(story);
          // PLAT-11224: cards appear at upload time; they open once the
          // transcript is ready, and the badge clears at full readiness.
          const isOpenable = isStoryEditorEntryReady(story);
          const processingLabel = isStoryReady(story)
            ? undefined
            : isOpenable
              ? 'Finishing video'
              : 'Processing';
          return (
            <HomeStoryCard
              key={story.id}
              id={story.id}
              title={story.title}
              thumbnail={getStoryThumbnail(story)}
              duration={getStoryDuration(story)}
              updatedDate={formatUpdateDate(story._publishedAt)}
              clips={getClipsCount(story)}
              articles={getArticlesCount(story)}
              dimensions={dimensions}
              processingLabel={processingLabel}
              onDimensionClick={aspectRatio => {
                const video = targetByRatio.get(aspectRatio);
                if (video) {
                  openDimensionPreview(story.id, aspectRatio, video);
                }
              }}
              onClick={isOpenable ? () => handleStoryClick(story) : undefined}
            />
          );
        })}
      </StoriesGrid>
    </Section>
  );
});

const Section = styled.div`
  margin-bottom: 48px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const SectionHeader = styled.h2`
  font-size: 20px;
  font-weight: var(--font-weight-semibold);
  color: var(--foreground);
  margin: 0;
`;

/* Secondary button, sm size — docs/design-system.md "Button Hierarchy". */
const ViewAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border-radius: 6px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  border: 1px solid var(--border);
  background-color: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  text-decoration: none;
  line-height: normal;
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast);

  &:hover {
    background-color: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;

const StoriesGrid = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  /* Each card takes equal space but never wider than 1/5 of the row */
  > * {
    flex: 1;
    min-width: 0;
    max-width: calc((100% - 64px) / 5);
  }

  /* Medium screens: hide 5th card, show 4 */
  @media (max-width: 1400px) {
    > * {
      max-width: calc((100% - 48px) / 4);
    }
    > *:nth-child(n + 5) {
      display: none;
    }
  }

  /* Smaller screens: hide 4th and 5th cards, show 3 */
  @media (max-width: 1100px) {
    > * {
      max-width: calc((100% - 32px) / 3);
    }
    > *:nth-child(n + 4) {
      display: none;
    }
  }

  /* Tablet: show 2 cards */
  @media (max-width: 800px) {
    > * {
      max-width: calc((100% - 16px) / 2);
      min-width: 160px;
    }
    > *:nth-child(n + 3) {
      display: none;
    }
  }

  /* Mobile: show 1 card full width */
  @media (max-width: 500px) {
    flex-direction: column;
    > * {
      max-width: 100%;
    }
    > *:nth-child(n + 2) {
      display: none;
    }
  }
`;

const EmptyText = styled.p`
  color: var(--text-muted);
  font-size: var(--text-sm);
`;
